import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  CarAggregatedAgeDetails,
  CarAggregatedComplianceDueDetails,
  RailCar,
  RailCarEndpointRes,
} from 'src/app/models/railcar.model';
import {
  CAR_TYPE,
  MarketPlaceQuoteRequestService,
} from '../../services/market-place-quote-request-service/market-place-quote-request.service';
import { HttpClient } from '@angular/common/http';
import { LoginService } from 'src/app/services/login-service/login.service';
import { ConfigService } from 'src/app/services/config-service/config.service';
import { REQUEST_STEPS } from 'src/app/routes/search-inventory-component/request-quote-component/enum-request-steps';
import { Router } from '@angular/router';
import { MixpanelService } from '../../services/mixpanel-service/mixpanel.service';

@Component({
  selector: 'app-new-builds-landing-page',
  templateUrl: './new-builds-landing-page.component.html',
  styleUrls: ['./new-builds-landing-page.component.scss'],
})
export class NewBuildsLandingPageComponent implements OnInit, OnDestroy {
  quoteControl = new FormControl({
    value: 'begin quote request',
    disabled: true,
  });

  isDisplayingDialog = false;
  detailViewObj: any | undefined;

  loading = false;
  loadingCount = 0; // Counter to track ongoing image loads

  public CAR_TYPE = CAR_TYPE;
  loadedData: CAR_TYPE = CAR_TYPE.COVERED_HOPPER;
  isDisabled = true;
  products: RailCar[] = [];
  selectedProducts: RailCar[] = []; // Array to store selected products
  imageSubjects: {
    [key: string]: BehaviorSubject<string | ArrayBuffer | null>;
  } = {};

  private subscriptions: Subscription[] = [];
  selectEnabled = true;
  constructor(
    private quoteRequestService: MarketPlaceQuoteRequestService,
    private httpClient: HttpClient,
    private loginService: LoginService,
    private configService: ConfigService,
    private router: Router,
    private mixpanelService: MixpanelService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscribeToNewBuildsData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openModel(car: any): void {
    this.isDisplayingDialog = true;
    this.detailViewObj = car;
  }

  closeModel(): void {
    this.isDisplayingDialog = false;
    this.detailViewObj = undefined;
  }

  carSelected(selectedCar: RailCar): void {
    this.isDisplayingDialog = false;
    console.log('Selected Car:', selectedCar);
    this.toggleSelect(selectedCar);
    this.beginQuoteRequest();
  }

  subscribeToNewBuildsData(): void {
    this.loading = true;
    this.subscriptions.push(
      this.quoteRequestService.newbuildsCarList$.subscribe(
        (carList: { [key: string]: RailCarEndpointRes }) => {
          const coveredHopperData = carList[CAR_TYPE.COVERED_HOPPER];

          if (coveredHopperData) {
            // Map aggregated details (age range and compliance due) to each product
            const aggregatedAgeRangeMap = new Map(
              coveredHopperData.carDetails.aggregated_age_range.map(
                (detail: CarAggregatedAgeDetails) => [detail.model_id, detail]
              )
            );

            const aggregatedComplianceDueMap = new Map(
              coveredHopperData.carDetails.aggregated_compliance_due.map(
                (detail: CarAggregatedComplianceDueDetails) => [
                  detail.model_id,
                  detail,
                ]
              )
            );

            // Update the products array with the aggregated information
            this.products = coveredHopperData.railCars.map(
              (product: RailCar) => {
                // Retrieve aggregated details for the current product
                const aggregatedAgeRange =
                  aggregatedAgeRangeMap.get(product.model_id) || null;
                const aggregatedComplianceDue =
                  aggregatedComplianceDueMap.get(product.model_id) || null;

                return {
                  ...product,
                  isSelected: false, // Default selection state
                  aggregated: {
                    aggregated_age_range: aggregatedAgeRange
                      ? [aggregatedAgeRange]
                      : [], // Ensure no undefined values
                    aggregated_compliance_due: aggregatedComplianceDue
                      ? [aggregatedComplianceDue]
                      : [], // Ensure no undefined values
                  },
                  aggregatedCars: [{ ...product }], // Aggregated cars is an array containing the product itself
                };
              }
            );

            this.cdr.detectChanges(); // Notify Angular of changes

            // Get unique file names
            const uniqueFileNames = Array.from(
              new Set(
                this.products.map((product: RailCar) => product.file_name)
              )
            );

            // Load images for unique file names only
            uniqueFileNames.forEach((fileName: string) => {
              this.loadImage(fileName);
            });
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {}, // Error handler (optional)
        () => {
          this.loading = false; // Complete
        }
      )
    );
  }

  loadImage(fileName: string): void {
    if (!this.imageSubjects[fileName]) {
      this.loadingCount++; // Increment the loading counter
      this.loading = true;
      this.imageSubjects[fileName] = new BehaviorSubject<
        string | ArrayBuffer | null
      >(null);
      const token = this.loginService.getSessionToken();
      if (token === null) this.loginService.signout();

      const image$ = this.httpClient.post(
        `${this.configService.imagesURL}/${fileName}`,
        {},
        {
          headers: { Authorization: 'Bearer ' + token },
          observe: 'response',
          responseType: 'blob',
        }
      );

      image$.subscribe({
        next: (img: any) => {
          const reader = new FileReader();
          reader.addEventListener(
            'load',
            () => {
              this.imageSubjects[fileName].next(reader.result);
              this.decrementLoadingCount(); // Decrement counter after successful load
            },
            false
          );

          if (img) {
            reader.readAsDataURL(img.body);
          } else {
            this.decrementLoadingCount(); // Ensure counter is decremented even if there's no image body
          }
        },
        error: () => {
          this.imageSubjects[fileName].next(null);
          this.decrementLoadingCount(); // Decrement counter on error
        },
      });
    }
  }

  decrementLoadingCount(): void {
    this.loadingCount--;
    if (this.loadingCount <= 0) {
      this.loading = false;
    }
  }

  getImageUrl(fileName: string): any {
    const imageUrl = this.imageSubjects[fileName]?.value;
    return imageUrl ? imageUrl : '/assets/images/image-coming-soon.png';
  }

  updateProductList(): void {
    if (this.loadedData === CAR_TYPE.COVERED_HOPPER) {
      this.subscriptions.push(
        this.quoteRequestService.newbuildsCarList$.subscribe((carList) => {
          if (carList && carList[CAR_TYPE.COVERED_HOPPER]) {
            this.products = carList[CAR_TYPE.COVERED_HOPPER].railCars;
            this.products.forEach((product) => {
              this.loadImage(product.file_name);
              this.cdr.detectChanges();
            });
          }
        })
      );
    } else {
      this.products = []; // For "Coming Soon" or other tabs
    }
  }

  trackByModelId(index: number, product: RailCar): number {
    return product.model_id;
  }

  toggleSelect(product: RailCar): void {
    product.isSelected = !product.isSelected;

    if (product.isSelected) {
      this.selectedProducts.push(product);
    } else {
      this.selectedProducts = this.selectedProducts.filter(
        (p) => p.model_id !== product.model_id
      );
    }
    this.updateQuoteButtonState();
    this.cdr.detectChanges();
  }

  updateQuoteButtonState(): void {
    this.selectEnabled = this.selectedProducts.length > 0;
    if (this.selectedProducts.length > 0) {
      this.quoteControl.enable();
    } else {
      this.quoteControl.disable();
    }
  }

  beginQuoteRequest(): void {
    if (this.selectedProducts.length === 0) return;

    const userEmail = localStorage.getItem('userEmail') || '';

    // Mixpanel Tracking
    this.mixpanelService.trackBeginTime(
      'Begin Quote Button Click Time',
      userEmail
    );
    this.mixpanelService.track({
      id: userEmail,
      eventName: 'Begin Quote',
      eventDisplayName: 'Begin Quote Request',
      kpi: 'User Engagement',
      trigger: 'On Button Click',
      eventDefinition: 'User is ready to start quoting process',
      propertyType: 'Event Property',
      propertyDefinition: 'Button Quote Submit',
    });

    // Save selected products and navigate to the next step
    this.quoteRequestService.setSelectedCarList(
      this.selectedProducts,
      [] // Assuming no specific car attributes here
    );
    this.quoteRequestService.setNextStep(REQUEST_STEPS.SELECTION);
    this.quoteRequestService.selectCarType(CAR_TYPE.COVERED_HOPPER);
    localStorage.setItem('fromNewBuilds', 'true');
    this.router.navigate(['/search-inventory/request-quote'], {
      state: { fromNewBuilds: true },
    });
  }

  truncateText(text: any, limit: number): string {
    return text?.length > limit ? text?.substring(0, limit) + '...' : text;
  }

  loadData(tab: CAR_TYPE): void {
    if (this.loadedData !== tab) {
      this.loadedData = tab;
      this.updateProductList();
    }
  }
}
